"use client";

import { cn } from "@/lib/utils";

export type FooterPadding = 'none' | 'sm' | 'md';

export interface ActionFooterProps {
  /** Padding around the footer */
  padding?: FooterPadding;
  /** Additional CSS classes */
  className?: string;
  /** Button/action children */
  children: React.ReactNode;
}

const paddingClasses: Record<FooterPadding, string> = {
  none: "",
  sm: "pt-2",
  md: "pt-4",
};

/**
 * ActionFooter - Container for action buttons at bottom of cards
 * 
 * Simple wrapper to maintain consistent spacing for action areas.
 * 
 * @example
 * <ActionFooter padding="md">
 *   <GlassButton fullWidth>Join Now</GlassButton>
 * </ActionFooter>
 */
export function ActionFooter({
  padding = "md",
  className,
  children,
}: ActionFooterProps) {
  return (
    <div className={cn("w-full", paddingClasses[padding], className)}>
      {children}
    </div>
  );
}

export type GlassButtonVariant = 'glass' | 'glass-solid';
export type IconPosition = 'left' | 'right';

export interface GlassButtonProps {
  /** Visual variant */
  variant?: GlassButtonVariant;
  /** Icon component */
  icon?: React.ComponentType<{ className?: string }>;
  /** Position of icon */
  iconPosition?: IconPosition;
  /** Whether button takes full width */
  fullWidth?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Button content */
  children: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Button type */
  type?: 'button' | 'submit';
}

const variantClasses: Record<GlassButtonVariant, string> = {
  glass: cn(
    "bg-white/20 backdrop-blur-sm border-white/30 text-white",
    "hover:bg-white hover:text-gray-900"
  ),
  "glass-solid": cn(
    "bg-white/90 backdrop-blur-sm border-white/50 text-gray-900",
    "hover:bg-white"
  ),
};

/**
 * GlassButton - Glassmorphism-style button for hero sections
 * 
 * Use on dark backgrounds (images, gradients) for primary actions.
 * 
 * @example
 * // On hero image
 * <GlassButton icon={ChevronRight} fullWidth>
 *   Join League
 * </GlassButton>
 * 
 * // Solid variant for more emphasis
 * <GlassButton variant="glass-solid" onClick={handleClick}>
 *   Get Started
 * </GlassButton>
 */
export function GlassButton({
  variant = "glass",
  icon: Icon,
  iconPosition = "right",
  fullWidth = false,
  className,
  children,
  onClick,
  disabled = false,
  type = "button",
}: GlassButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2",
        "px-4 h-11 rounded-lg border",
        "font-semibold transition-all duration-300",
        variantClasses[variant],
        fullWidth && "w-full",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {Icon && iconPosition === "left" && (
        <Icon className="h-4 w-4" />
      )}
      {children}
      {Icon && iconPosition === "right" && (
        <Icon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      )}
    </button>
  );
}

export default ActionFooter;

